// ink-component
import React, { ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';
import Ornament, { OrnamentProps } from '../Ornament/Ornament';
import Text from '../Typography/Text/Text';
import { ListItemWrapper, StyledListItem, ListItemInfo, OrnamentWrapper } from './styles';
import { FeedbackVariants, OrnamentVariantColorTypes } from '../_common/Types/GlobalTypes';
import { IconTypes } from '../../helpers/constants/icons';
import getGlobalProps from '../../helpers/constants/global-props';
import useWindowWidth from '../../hooks/useWindowWidth';

export interface ListItemProps {
  /** Semantic variants, based on intent. Use `feedback-neutral` and
   * `feedback-positive` for binary to-dos. Use `feedback-pending`,
   * `feedback-notice`, and `feedback-negative` for intermediary states in a
   * progression. Use `feedback-informational` for all other cases. [Read more
   * about ListItem variants](/components/ListItem/usage). */
  variant?: Extract<
    FeedbackVariants,
    | 'feedback-positive'
    | 'feedback-negative'
    | 'feedback-notice'
    | 'feedback-neutral'
    | 'feedback-pending'
    | 'feedback-informational'
    | 'feedback-loading'
  >;
  /** Modifier for different ornament sizes */
  size?: OrnamentProps['size'];
  /** Title */
  title?: ReactNode;
  /** Contents. Usually wrapped around ListControl or ListBody components */
  children?: ReactNode;
  /** Subtitle of the item */
  text?: ReactNode;
  /** Add indentation to ListItem that has no icon */
  indent?: '1' | '2';
  /** Modifier for banner items */
  isBanner?: boolean;
  /** Prevents stacking of controls on mobile devices */
  horizontalMobileControls?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used.  */
  statusIcon?: IconTypes;
  /** @deprecated:variant This prop is deprecated and should not be used.  */
  statusColor?: Exclude<OrnamentVariantColorTypes, 'brown'>;
  /** @deprecated:variant This prop is deprecated and should not be used.  */
  statusInverse?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used.  */
  statusDashed?: boolean;
  /** @deprecated:variant This prop is deprecated and should not be used. */
  statusSpinner?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Splits content into items, with optional icons for visual support
 * @categories layout
 * @group List
 */
function ListItem(props: ListItemProps) {
  const {
    title,
    children,
    text,
    indent,
    isBanner,
    variant,
    horizontalMobileControls,
    size,
    statusColor,
    statusIcon,
    statusInverse,
    statusDashed,
    statusSpinner,
    'data-testid': dataTestId,
  } = props;

  let globalProps = {};

  const { isMobile } = useWindowWidth();

  if (statusColor || statusIcon || statusDashed || statusInverse || statusSpinner) {
    globalProps = getGlobalProps({ deprecatedProps: true });
  }

  if (variant && (statusColor || statusIcon || statusDashed || statusInverse || statusSpinner)) {
    throw new Error(`[_ink] Deprecated props should not be used with 'variant' in ListItem`);
  }

  if (statusColor) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `statusColor` prop in ListItem is deprecated. Use `variant` instead. https://ink.carta.com/components/ListItem/usage',
    );
  }

  if (statusIcon) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `statusIcon` prop in ListItem is deprecated. Use `variant` instead. https://ink.carta.com/components/ListItem/usage',
    );
  }

  if (statusInverse) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `statusInverse` prop in ListItem is deprecated. Use `variant` instead. https://ink.carta.com/components/ListItem/usage',
    );
  }

  if (statusDashed) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `statusDashed` prop in ListItem is deprecated. Use `variant` instead. https://ink.carta.com/components/ListItem/usage',
    );
  }

  if (statusSpinner) {
    console.warn(
      '[INK DEPRECATION WARNING]: The `statusSpinner` prop in ListItem is deprecated. Use `variant` instead. https://ink.carta.com/components/ListItem/usage',
    );
  }

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        listItemBanner: isBanner,
        listItemHasIcon: statusColor || statusIcon || variant,
        listItemHasText: Boolean(text),
        listItemIndent: indent && (!statusColor || !statusIcon || !variant) ? indent : null,
        listItemSize: size,
        listItemMobile: !horizontalMobileControls && isMobile,
      })}
    >
      <ListItemWrapper data-testid={dataTestId} {...globalProps}>
        {!isBanner && (
          <StyledListItem>
            {(statusIcon || statusColor || variant) && (
              <OrnamentWrapper>
                <Ornament
                  color={statusColor}
                  icon={statusIcon}
                  isDashed={statusDashed}
                  isInverse={statusInverse}
                  isSpinner={statusSpinner}
                  size={size}
                  variant={variant}
                />
              </OrnamentWrapper>
            )}
            {Boolean(title) && (
              <ListItemInfo>
                <Text variant="body-2" trim>
                  {title}
                </Text>
                {Boolean(text) && (
                  <Text variant="body-3-feedback-neutral" trim>
                    {text}
                  </Text>
                )}
              </ListItemInfo>
            )}
          </StyledListItem>
        )}
        {children}
      </ListItemWrapper>
    </ThemeProvider>
  );
}

ListItem.defaultProps = {
  children: null,
  indent: null,
  isBanner: false,
  statusDashed: false,
  statusInverse: false,
  statusSpinner: false,
  horizontalMobileControls: false,
  text: '',
  title: '',
  size: 'large',
};

ListItem.displayName = 'ListItem';

export default ListItem;
